'use client';

import { type ReactNode, createContext, useContext, useMemo } from 'react';

type SlotPosition = 'start' | 'end';

interface FormFieldSlotContextValue {
  position: SlotPosition;
  isDisabled?: boolean;
}

const FormFieldSlotContext = createContext<FormFieldSlotContextValue | null>(
  null
);

function useFormFieldSlotContext(): FormFieldSlotContextValue {
  const context = useContext(FormFieldSlotContext);
  if (context === null) {
    throw new Error(
      'FormFieldSlot must be used inside a FormFieldSlotContainer.'
    );
  }
  return context;
}

interface FormFieldSlotContainerProps {
  position: SlotPosition;
  isDisabled?: boolean;
  children: ReactNode;
}

const FormFieldSlotContainer = ({
  position,
  isDisabled,
  children,
}: FormFieldSlotContainerProps) => {
  const value = useMemo(
    () => ({ position, isDisabled }),
    [position, isDisabled]
  );
  return (
    <FormFieldSlotContext.Provider value={value}>
      {children}
    </FormFieldSlotContext.Provider>
  );
};

FormFieldSlotContainer.displayName = 'FormFieldSlotContainer';

export {
  FormFieldSlotContainer,
  useFormFieldSlotContext,
  type FormFieldSlotContextValue,
  type FormFieldSlotContainerProps,
  type SlotPosition,
};
